import { useCallback, useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import {
  CustomerStatus,
  MAX_OPTICAL_JOBS_PER_ORDER,
  PERMISSIONS,
  type BasketValidation,
  type CustomerSummary,
  type ValidatedBasketLine,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { useBranch } from '@/hooks/useBranch';
import { useLocale } from '@/hooks/useLocale';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorDetails, isNetworkError } from '@/services/apiClient';
import { stableStringify } from '@/services/operationKey';
import { formatCount } from '@/utils/format';
import { useProductQuery } from '@/modules/catalog/api/catalog.api';
import { useCustomerQuery } from '@/modules/customers/api/customers.api';
import type { FieldError } from '@/modules/medical/examinationForm';
import { useUnsavedChangesGuard } from '@/modules/medical/useUnsavedChangesGuard';
import {
  salesKeys,
  useCreateCounterSaleMutation,
  useSalesSettingsQuery,
  validateBasket,
} from '@/modules/sales/api/sales.api';
import { creditTermsProblems } from '@/modules/sales/creditTerms';
import { readHandoff } from '@/modules/sales/recommendationHandoff';
import {
  opticalOrderKeys,
  useBuilderValidationQuery,
  useDispensingPrescriptionsQuery,
  useOpticalLabsQuery,
} from '../api/opticalOrders.api';
import {
  BUILDER_STEPS,
  PAIR_STEPS,
  builderReducer,
  checkoutPayload,
  createDraft,
  pairComplete,
  pairsWithUnusableClinical,
  reachable,
  stepComplete,
  validationRequest,
  type BuilderAction,
  type BuilderStep,
  type PairDraft,
  type SelectedFrame,
} from '../builder/draft';
import {
  handoffRefusal,
  recommendedFrame,
  refusedRecommendationPairs,
  type HandoffOutcome,
} from '../builder/recommendation';
import { problemsFromRefusal, type BuilderProblem } from '../builder/refusals';
import { OrderContext } from '../components/OrderContext';
import { PairTabs } from '../components/PairTabs';
import { StepIndicator } from '../components/StepIndicator';
import { FrameStep } from '../steps/FrameStep';
import { LensesStep, type PairVerdict } from '../steps/LensesStep';
import { PaymentStep } from '../steps/PaymentStep';
import { PrescriptionStep } from '../steps/PrescriptionStep';
import { ReviewStep } from '../steps/ReviewStep';

/** The steps that ask the server about the pairs as they stand. */
const CHECKED_STEPS: readonly BuilderStep[] = ['lenses', 'review', 'payment'];

/** Holds a value back until it has stopped changing, so typing a fitting height asks the server once, not per keystroke. */
function useSettled<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/** Problems the server's line check raises, placed on the frame or lens they concern. A price change is handled on the review. */
function lineProblems(
  lines: readonly ValidatedBasketLine[],
  pairs: readonly PairDraft[],
): BuilderProblem[] {
  return lines.flatMap((line) => {
    const pair = pairs.find((candidate) =>
      Object.values(candidate.lineKeys).includes(line.lineKey ?? ''),
    );
    if (!pair) return [];
    const role =
      line.lineKey === pair.lineKeys.frame
        ? 'frame'
        : line.lineKey === pair.lineKeys.right
          ? 'right'
          : 'left';
    return line.issues
      .filter((issue) => issue !== 'PRICE_CHANGED')
      .map((issue): BuilderProblem => ({
        pairId: pair.id,
        step: role === 'frame' ? 'frame' : 'lenses',
        field: role === 'frame' ? 'frame' : `lens.${role}`,
        message: {
          key: `opticalOrders.review.issues.${issue}`,
          values: { count: String(line.availableQuantity) },
        },
      }));
  });
}

/**
 * The optical order builder: one customer, one route, five steps, one to four pairs.
 *
 * The draft lives in this component's memory and nowhere else. Every commercial figure comes from the server's check of the order as it stands, and the confirmation is the counter's own checkout — the same idempotent request, so a retried confirmation cannot place a second order.
 */
export function OpticalOrderBuilderPage() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { customerId = '' } = useParams<{ customerId: string }>();
  const customer = useCustomerQuery(customerId);
  const BackIcon = isRtl ? ArrowRight : ArrowLeft;

  // A frame sent from the recommendations travels once, in this entry's router state: read on the first render and then cleared, so Back, Forward, a refresh or React's double rendering can never take it twice.
  const [handoff] = useState(() => readHandoff(location.state));
  const refusal = handoff ? handoffRefusal(handoff, customerId) : null;
  // The catalogue as it is now, not as the recommendation showed it: a frame no longer sold is not put in the order.
  const product = useProductQuery(handoff && !refusal ? handoff.productId : undefined);
  useEffect(() => {
    if (readHandoff(location.state) === null) return;
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null },
    );
  }, [location.state, location.pathname, location.search, location.hash, navigate]);

  if (customer.isPending || (handoff !== null && refusal === null && product.isPending)) {
    return <LoadingState />;
  }
  if (customer.isError || !customer.data) {
    return (
      <div className="space-y-4">
        <Alert tone="error">{t('opticalOrders.builder.loadError')}</Alert>
        <Link
          to="/customers"
          className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm"
        >
          <BackIcon className="size-4" aria-hidden />
          {t('customers.backToList')}
        </Link>
      </div>
    );
  }

  const seedFrame =
    handoff && refusal === null && product.data ? recommendedFrame(handoff, product.data) : null;
  const handoffOutcome: HandoffOutcome | null = handoff
    ? (refusal ?? (seedFrame ? 'seeded' : 'unavailable'))
    : null;

  return (
    <OpticalOrderBuilder
      key={customer.data.id}
      customer={customer.data}
      seedFrame={seedFrame}
      handoffOutcome={handoffOutcome}
    />
  );
}

function OpticalOrderBuilder({
  customer,
  seedFrame,
  handoffOutcome,
}: {
  customer: CustomerSummary;
  /** The recommended frame for the first pair, with its selection token; null when the builder was not opened from a recommendation or the frame could not be taken. */
  seedFrame: SelectedFrame | null;
  handoffOutcome: HandoffOutcome | null;
}) {
  const { t } = useTranslation();
  const { isRtl, locale } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const { activeBranch } = useBranch();
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Read once, when the builder opens: a recommended frame goes to the first pair only, and nothing re-applies it later.
  const [draft, dispatch] = useReducer(builderReducer, undefined, () =>
    createDraft(seedFrame ? { frame: seedFrame } : undefined),
  );
  /** Pairs whose recommendation checkout could not confirm, waiting for the user to decide. */
  const [refusedPairs, setRefusedPairs] = useState<string[]>([]);
  // A refused recommendation that was already let go — its frame changed, or its pair removed — needs no decision any more.
  const stillRefused = refusedPairs.filter((pairId) =>
    draft.pairs.some((pair) => pair.id === pairId && pair.frame?.recommendationToken),
  );
  // The seeded sentence says the recommended frame is the first pair's; once no pair carries it, the frame step's own notice says what happened instead.
  const arrival =
    handoffOutcome === 'seeded' && !draft.pairs.some((pair) => pair.frame?.recommendationToken)
      ? null
      : handoffOutcome;
  const [showErrors, setShowErrors] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'error' | 'warning'; message: FieldError } | null>(
    null,
  );
  const [confirmProblems, setConfirmProblems] = useState<BuilderProblem[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'checking' | 'confirming'>('idle');
  const [approved, setApproved] = useState<string | null>(null);
  const submitting = useRef(false);
  /** The confirmation whose answer never arrived: sent again unchanged, with the same key, and nothing asked first. */
  const unanswered = useRef<string | null>(null);

  const settings = useSalesSettingsQuery();
  const labs = useOpticalLabsQuery();
  const createSale = useCreateCounterSaleMutation();
  const guard = useUnsavedChangesGuard(draft.touched);
  const usesClinical = draft.pairs.some((pair) => pair.prescription.source === 'CLINICAL');
  const usable = useDispensingPrescriptionsQuery(customer.id, false, usesClinical);
  const refetchUsable = usable.refetch;
  // Whether the customer has any usable prescription from this shop; with none, every pair that has no source yet starts on external.
  const shopPrescriptions = useDispensingPrescriptionsQuery(customer.id);
  const noShopPrescription = shopPrescriptions.isSuccess && shopPrescriptions.data.length === 0;
  const unsourcedPairIds = draft.pairs
    .filter((pair) => pair.prescription.source === null)
    .map((pair) => pair.id)
    .join(',');
  useEffect(() => {
    if (noShopPrescription && unsourcedPairIds !== '') {
      dispatch({ type: 'preferExternal', pairIds: unsourcedPairIds.split(',') });
    }
  }, [noShopPrescription, unsourcedPairIds]);

  const act = useCallback((action: BuilderAction) => {
    if (action.type !== 'goTo' && action.type !== 'selectPair') setConfirmProblems([]);
    dispatch(action);
  }, []);

  const step = draft.step;
  const activePair = draft.pairs.find((pair) => pair.id === draft.activePairId) ?? draft.pairs[0];
  const activeNumber = activePair ? draft.pairs.indexOf(activePair) + 1 : 1;

  // ---- The server's check of the order as it stands ----

  const settledPairs = useSettled(draft.pairs, 300);
  const reviewRequest = useMemo(
    () => validationRequest(settledPairs, customer.id),
    [settledPairs, customer.id],
  );
  const reviewFingerprint = reviewRequest ? stableStringify(reviewRequest.payload) : null;
  const review = useBuilderValidationQuery(
    reviewRequest?.payload ?? null,
    Boolean(activeBranch) && CHECKED_STEPS.includes(step),
  );
  const totalMinor = review.data?.totalMinor ?? 0;
  const tendered = draft.tenders.reduce((sum, tender) => sum + tender.amountMinor, 0);
  const remaining = Math.max(0, totalMinor - tendered);
  const financed = draft.credit ? remaining : 0;
  const settledFinanced = useSettled(financed, 300);
  const creditRequest = useMemo(
    () =>
      settledFinanced > 0 ? validationRequest(settledPairs, customer.id, settledFinanced) : null,
    [settledPairs, customer.id, settledFinanced],
  );
  const creditCheck = useBuilderValidationQuery(
    creditRequest?.payload ?? null,
    Boolean(activeBranch) && step === 'payment',
  );

  // While a new check runs, the last answer stays on screen; its refusals name jobs by place, so they are read against the pairs that answer was about, not the pairs being asked about now.
  const [answeredPairIds, setAnsweredPairIds] = useState<readonly string[]>([]);
  useEffect(() => {
    if (review.data && !review.isPlaceholderData && reviewRequest) {
      setAnsweredPairIds(reviewRequest.pairIds);
    }
  }, [review.data, review.isPlaceholderData, reviewRequest]);

  const refusalProblems = useMemo(() => {
    if (!reviewRequest) return [];
    const refusal = review.data?.optical?.refusal;
    if (refusal) {
      return problemsFromRefusal(
        refusal,
        review.isPlaceholderData ? answeredPairIds : reviewRequest.pairIds,
      );
    }
    if (review.isError && apiErrorCode(review.error) === 'VALIDATION_FAILED') {
      return problemsFromRefusal(
        { code: 'VALIDATION_FAILED', details: apiErrorDetails(review.error) },
        reviewRequest.pairIds,
      );
    }
    return [];
  }, [
    review.data,
    review.isPlaceholderData,
    review.isError,
    review.error,
    reviewRequest,
    answeredPairIds,
  ]);

  const serverProblems = confirmProblems.length > 0 ? confirmProblems : refusalProblems;
  const stepProblems = useMemo(
    () => [...serverProblems, ...lineProblems(review.data?.lines ?? [], draft.pairs)],
    [serverProblems, review.data, draft.pairs],
  );

  const allWhole = draft.pairs.every(pairComplete);
  const reviewClean =
    allWhole &&
    review.data !== undefined &&
    !review.isFetching &&
    !review.isError &&
    review.data.isUnchanged &&
    reviewRequest?.pairIds.length === draft.pairs.length &&
    confirmProblems.length === 0;
  const paymentReachable = approved !== null && approved === reviewFingerprint && allWhole;

  // ---- A chosen clinical prescription that stopped being usable is let go, never replaced ----

  useEffect(() => {
    if (!usable.data) return;
    const affected = pairsWithUnusableClinical(
      draft,
      usable.data.map((entry) => entry.clinicalPrescriptionId),
    );
    if (affected.length > 0) act({ type: 'clinicalUnavailable', pairIds: affected });
  }, [usable.data, draft, act]);

  useEffect(() => {
    const named = refusalProblems
      .filter((problem) => problem.field === 'clinical' && problem.pairId)
      .map((problem) => problem.pairId as string);
    if (named.length > 0) {
      act({ type: 'clinicalUnavailable', pairIds: named });
      void refetchUsable();
    } else if (review.data?.optical?.refusal?.code === 'NOT_FOUND') {
      void refetchUsable();
    }
  }, [refusalProblems, review.data, act, refetchUsable]);

  // ---- Steps ----

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [step, draft.activePairId]);

  const goTo = (target: BuilderStep, pairId?: string) => {
    setShowErrors(false);
    act({ type: 'goTo', step: target, pairId });
  };

  const canOpen = (target: BuilderStep) => {
    if (target === 'payment') return paymentReachable;
    return reachable(draft, target);
  };

  const isDone = (target: BuilderStep) => {
    if (PAIR_STEPS.includes(target)) return draft.pairs.every((pair) => stepComplete(pair, target));
    if (target === 'review') return paymentReachable;
    return false;
  };

  const next = () => {
    setNotice(null);
    if (!activePair) return;
    if (PAIR_STEPS.includes(step)) {
      if (!stepComplete(activePair, step)) {
        setShowErrors(true);
        return;
      }
      if (step === 'prescription') return goTo('frame');
      if (step === 'frame') return goTo('lenses');
      const unfinished = draft.pairs.find((pair) => !pairComplete(pair));
      if (unfinished) {
        const target =
          PAIR_STEPS.find((candidate) => !stepComplete(unfinished, candidate)) ?? 'prescription';
        setNotice({
          tone: 'warning',
          message: {
            key: 'opticalOrders.pairs.finishPair',
            values: { number: String(draft.pairs.indexOf(unfinished) + 1) },
          },
        });
        return goTo(target, unfinished.id);
      }
      return goTo('review');
    }
    if (step === 'review' && reviewClean && reviewFingerprint) {
      setApproved(reviewFingerprint);
      goTo('payment');
    }
  };

  const back = () => {
    setNotice(null);
    const index = BUILDER_STEPS.indexOf(step);
    const previous = BUILDER_STEPS[index - 1];
    if (previous) goTo(previous);
  };

  // ---- Payment and confirmation ----

  const policy = settings.data?.creditLimitPolicy;
  const creditIssues = creditCheck.data?.customerIssues ?? [];
  const limitExceeded = creditIssues.includes('CREDIT_LIMIT_EXCEEDED');
  const canOverride = can(PERMISSIONS.SALES_CREDIT_OVERRIDE);
  const showOverride = limitExceeded && policy === 'ALLOW_WITH_OVERRIDE' && canOverride;
  const creditErrors = draft.credit
    ? creditTermsProblems(draft.credit, settings.data?.maxInstallments ?? 12, showOverride)
    : {};
  const creditBlocked =
    draft.credit !== null &&
    (Object.keys(creditErrors).length > 0 ||
      creditCheck.isFetching ||
      creditIssues.includes('CREDIT_UNAVAILABLE') ||
      (limitExceeded && policy === 'BLOCK') ||
      (limitExceeded && policy === 'ALLOW_WITH_OVERRIDE' && !canOverride));

  const confirmDisabled =
    !activeBranch ||
    !paymentReachable ||
    phase !== 'idle' ||
    review.isFetching ||
    tendered > totalMinor ||
    creditBlocked ||
    stillRefused.length > 0 ||
    customer.status !== CustomerStatus.ACTIVE;

  const blocking = (fresh: BasketValidation) =>
    fresh.lines.some((line) => line.issues.length > 0) ||
    Boolean(fresh.optical?.refusal) ||
    fresh.customerIssues.some(
      (issue) =>
        issue === 'MISSING' ||
        issue === 'INACTIVE' ||
        issue === 'CREDIT_UNAVAILABLE' ||
        (issue === 'CREDIT_LIMIT_EXCEEDED' && policy === 'BLOCK'),
    );

  const recover = (error: unknown, stage: 'check' | 'checkout', fingerprint: string) => {
    const code = apiErrorCode(error);
    // Only a lost answer or one still being worked on can be asked again as it was; any other answer settles the attempt.
    unanswered.current =
      stage === 'checkout' && (isNetworkError(error) || code === 'REQUEST_IN_PROGRESS')
        ? fingerprint
        : null;
    if (isNetworkError(error)) {
      setNotice({
        tone: 'error',
        message: {
          key:
            stage === 'check'
              ? 'opticalOrders.confirm.checkNetwork'
              : 'opticalOrders.confirm.network',
        },
      });
      return;
    }
    if (code === 'REQUEST_IN_PROGRESS') {
      setNotice({ tone: 'warning', message: { key: 'opticalOrders.confirm.inProgress' } });
      return;
    }
    if (code === 'RECOMMENDATION_PROVENANCE_INVALID') {
      // Nothing was placed. The recommendation is removed only if the user chooses to; the frame itself may still be sold.
      setRefusedPairs(
        refusedRecommendationPairs(
          apiErrorDetails(error).map((detail) => detail.field),
          draft.pairs,
        ),
      );
      setNotice(null);
      return;
    }
    const problems = problemsFromRefusal(
      { code, details: apiErrorDetails(error) },
      draft.pairs.map((pair) => pair.id),
    );
    if (code === 'CLINICAL_PRESCRIPTION_NOT_USABLE' || code === 'NOT_FOUND') {
      const named = problems.filter((problem) => problem.field === 'clinical' && problem.pairId);
      const pairIds = named.map((problem) => problem.pairId as string);
      if (pairIds.length > 0) act({ type: 'clinicalUnavailable', pairIds });
      void refetchUsable();
      setNotice({ tone: 'error', message: { key: 'opticalOrders.clinical.noLongerAvailable' } });
      const first =
        pairIds[0] ??
        draft.pairs.find((pair) => pair.prescription.source === 'CLINICAL')?.id ??
        draft.activePairId;
      goTo('prescription', first);
      return;
    }
    if (problems.some((problem) => problem.step === 'payment')) {
      setConfirmProblems(problems);
      setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.paymentRefused' } });
      return;
    }
    if (problems.length > 0) {
      setConfirmProblems(problems);
      setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.refused' } });
      goTo('review');
      return;
    }
    if (code === 'CONFLICT') {
      setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.stockChanged' } });
      void queryClient.invalidateQueries({ queryKey: opticalOrderKeys.all });
      goTo('review');
      return;
    }
    if (code === 'PERMISSION_DENIED' || code === 'FORBIDDEN') {
      setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.permission' } });
      return;
    }
    if (code === 'BRANCH_CONTEXT_MISSING' || code === 'BRANCH_ACCESS_DENIED') {
      setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.branch' } });
      return;
    }
    setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.failed' } });
  };

  const confirm = async () => {
    // A second click, or Enter held down, lands here while the first is still running and is ignored.
    if (submitting.current || !activeBranch) return;
    const payload = checkoutPayload(draft, customer.id);
    const check = validationRequest(draft.pairs, customer.id, financed);
    if (!payload || !check) return;
    const fingerprint = stableStringify(payload);

    submitting.current = true;
    setNotice(null);
    setConfirmProblems([]);
    setRefusedPairs([]);
    let stage: 'check' | 'checkout' = 'check';
    let placed = false;
    try {
      // Asked again now: a price, a lens or the stock may have moved since the review. Not before resending a confirmation whose answer was lost — that one may already have been placed, and only the same request with the same key can say so.
      setPhase('checking');
      const fresh = unanswered.current === fingerprint ? null : await validateBasket(check.payload);
      if (fresh && blocking(fresh)) {
        setApproved(null);
        void queryClient.invalidateQueries({ queryKey: opticalOrderKeys.all });
        const creditRefused = fresh.customerIssues.some(
          (issue) => issue === 'CREDIT_UNAVAILABLE' || issue === 'CREDIT_LIMIT_EXCEEDED',
        );
        if (creditRefused) {
          setNotice({ tone: 'error', message: { key: 'opticalOrders.confirm.creditRefused' } });
        } else {
          setNotice({ tone: 'warning', message: { key: 'opticalOrders.confirm.changed' } });
          goTo('review');
        }
        return;
      }

      stage = 'checkout';
      setPhase('confirming');
      const order = await createSale.mutateAsync(payload);
      // Placed: the builder takes no second confirmation, not even in the moment before it has left the screen.
      placed = true;
      unanswered.current = null;
      queryClient.setQueryData(salesKeys.order(order.id), order);
      // The draft is done with: leaving now is the point, not something to warn about.
      guard.allowNextNavigation();
      void navigate(`/sales/optical-orders/${order.id}`, {
        replace: true,
        state: { created: true },
      });
    } catch (error) {
      recover(error, stage, fingerprint);
    } finally {
      if (!placed) {
        submitting.current = false;
        setPhase('idle');
      }
    }
  };

  // ---- What each step shows ----

  const verdict: PairVerdict = (() => {
    if (!activePair || !pairComplete(activePair)) return 'incomplete';
    if (!activeBranch) return 'unavailable';
    if (review.isFetching || !reviewRequest?.pairIds.includes(activePair.id)) return 'checking';
    if (review.isError && apiErrorCode(review.error) !== 'VALIDATION_FAILED') return 'unavailable';
    return stepProblems.some((problem) => problem.pairId === activePair.id) ? 'problems' : 'ok';
  })();
  const pairProblems = activePair
    ? stepProblems.filter((problem) => problem.pairId === activePair.id && problem.step === step)
    : [];
  const prescriptionNumbers = useMemo(
    () =>
      new Map(
        (usable.data ?? []).map((entry) => [
          entry.clinicalPrescriptionId,
          entry.prescriptionNumber,
        ]),
      ),
    [usable.data],
  );
  const copyFrom = activePair
    ? draft.pairs
        .map((pair, index) => ({ pair, number: index + 1 }))
        .filter(({ pair }) => pair.id !== activePair.id && pair.prescription.source !== null)
        .map(({ pair, number }) => ({ pairId: pair.id, number }))
    : [];

  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  const NextIcon = isRtl ? ArrowLeft : ArrowRight;
  const removingNumber = removing ? draft.pairs.findIndex((pair) => pair.id === removing) + 1 : 0;
  const dialog = guard.isBlocked ? 'leave' : removing ? 'remove' : null;

  return (
    <div className="space-y-5">
      <Link
        to={`/customers/${customer.id}`}
        className="text-muted hover:text-ink inline-flex items-center gap-1.5 text-sm"
      >
        <BackIcon className="size-4" aria-hidden />
        {t('opticalOrders.builder.backToCustomer', { name: customer.displayName })}
      </Link>

      <PageHeader
        title={t('opticalOrders.builder.title')}
        subtitle={t('opticalOrders.builder.subtitle')}
      />

      <OrderContext customer={customer} />
      {customer.status !== CustomerStatus.ACTIVE ? (
        <Alert tone="warning">{t('opticalOrders.builder.inactiveCustomer')}</Alert>
      ) : null}
      {arrival && (step === 'prescription' || step === 'frame') ? (
        <Alert tone={arrival === 'seeded' ? 'info' : 'warning'}>
          {t(`opticalOrders.recommendation.arrival.${arrival}`)}
        </Alert>
      ) : null}

      <StepIndicator
        current={step}
        canOpen={canOpen}
        isDone={isDone}
        onOpen={(target) => goTo(target)}
      />

      {notice ? (
        <Alert tone={notice.tone}>{t(notice.message.key, notice.message.values)}</Alert>
      ) : null}

      {PAIR_STEPS.includes(step) ? (
        <PairTabs
          pairs={draft.pairs}
          activePairId={draft.activePairId}
          onSelect={(pairId) => act({ type: 'selectPair', pairId })}
        />
      ) : null}

      <section aria-labelledby={headingId} className="space-y-4">
        <h2
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="text-ink text-lg font-semibold focus:outline-none"
        >
          {t(`opticalOrders.steps.${step}`)}
          {PAIR_STEPS.includes(step) && draft.pairs.length > 1
            ? ` · ${t('opticalOrders.pairs.pair', { number: activeNumber })}`
            : ''}
          {PAIR_STEPS.includes(step) && draft.pairs.length > 1 && activePair?.usage
            ? ` · ${t(`opticalOrders.usage.pairLabel.${activePair.usage}`)}`
            : ''}
        </h2>

        {step === 'prescription' && activePair ? (
          <PrescriptionStep
            customerId={customer.id}
            pair={activePair}
            copyFrom={copyFrom}
            canAddPair={draft.pairs.length < MAX_OPTICAL_JOBS_PER_ORDER}
            dispatch={act}
            problems={pairProblems}
            showErrors={showErrors}
          />
        ) : null}
        {step === 'frame' && activePair ? (
          <FrameStep
            pair={activePair}
            dispatch={act}
            problems={pairProblems}
            showErrors={showErrors}
          />
        ) : null}
        {step === 'lenses' && activePair ? (
          <LensesStep
            pair={activePair}
            dispatch={act}
            problems={pairProblems}
            showErrors={showErrors}
            labs={labs.data ?? []}
            verdict={verdict}
          />
        ) : null}
        {step === 'review' ? (
          <ReviewStep
            draft={draft}
            validation={review.data}
            isChecking={review.isFetching}
            checkFailed={review.isError && apiErrorCode(review.error) !== 'VALIDATION_FAILED'}
            onRetry={() => void review.refetch()}
            problems={serverProblems}
            prescriptionNumbers={prescriptionNumbers}
            labs={labs.data ?? []}
            onEdit={(pairId, target) => goTo(target, pairId)}
            onAddPair={() => {
              setShowErrors(false);
              act({ type: 'addPair' });
            }}
            onRemovePair={setRemoving}
            onAcceptPrices={() => {
              const prices: Record<string, number> = {};
              for (const line of review.data?.lines ?? []) {
                if (line.lineKey) prices[line.lineKey] = line.unitPriceMinor;
              }
              act({ type: 'acceptPrices', prices });
            }}
          />
        ) : null}
        {step === 'payment' && stillRefused.length > 0 ? (
          <Alert tone="warning">
            <p>
              {t('opticalOrders.recommendation.refused', {
                pairs: stillRefused
                  .map((pairId) =>
                    formatCount(draft.pairs.findIndex((pair) => pair.id === pairId) + 1, locale),
                  )
                  .join(isRtl ? '، ' : ', '),
                count: stillRefused.length,
              })}
            </p>
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              onClick={() => {
                for (const pairId of stillRefused) act({ type: 'removeRecommendation', pairId });
                setRefusedPairs([]);
                setNotice({
                  tone: 'warning',
                  message: { key: 'opticalOrders.recommendation.removed' },
                });
              }}
            >
              {t('opticalOrders.recommendation.removeAndContinue')}
            </Button>
          </Alert>
        ) : null}
        {step === 'payment' ? (
          <PaymentStep
            totalMinor={totalMinor}
            tenders={draft.tenders}
            onTendersChange={(tenders) => act({ type: 'setTenders', tenders })}
            credit={draft.credit}
            onCreditChange={(credit) => act({ type: 'setCredit', credit })}
            settings={settings.data}
            creditIssues={creditIssues}
            showOverride={showOverride}
            creditErrors={creditErrors}
            problems={serverProblems.filter((problem) => problem.step === 'payment')}
            branch={activeBranch}
            onConfirm={() => void confirm()}
            isConfirming={phase !== 'idle'}
            confirmDisabled={confirmDisabled}
          />
        ) : null}
      </section>

      <div className="border-line flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        {step !== 'prescription' ? (
          <Button variant="secondary" onClick={back}>
            <BackIcon className="size-4" aria-hidden />
            {t('opticalOrders.steps.back')}
          </Button>
        ) : (
          <span />
        )}
        {step === 'review' ? (
          <div className="flex flex-wrap items-center gap-3">
            {!reviewClean && !review.isFetching ? (
              <span className="text-muted text-sm">{t('opticalOrders.review.fixFirst')}</span>
            ) : null}
            <Button onClick={next} disabled={!reviewClean || !activeBranch}>
              {t('opticalOrders.steps.toPayment')}
              <NextIcon className="size-4" aria-hidden />
            </Button>
          </div>
        ) : step !== 'payment' ? (
          <Button onClick={next}>
            {step === 'lenses' &&
            draft.pairs.every((pair) => pair.id === activePair?.id || pairComplete(pair))
              ? t('opticalOrders.steps.toReview')
              : t('opticalOrders.steps.continue')}
            <NextIcon className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      {/* One dialog at a time, mounted only while it is asked: the shared dialog names itself by a fixed id, so two at once would share a title. */}
      {dialog === 'leave' ? (
        <ConfirmDialog
          isOpen
          isDestructive
          title={t('opticalOrders.leave.title')}
          message={t('opticalOrders.leave.message')}
          confirmLabel={t('opticalOrders.leave.confirm')}
          cancelLabel={t('opticalOrders.leave.stay')}
          onCancel={guard.stay}
          onConfirm={guard.leave}
        />
      ) : null}
      {dialog === 'remove' && removing ? (
        <ConfirmDialog
          isOpen
          isDestructive
          title={t('opticalOrders.pairs.removeTitle', { number: removingNumber })}
          message={t('opticalOrders.pairs.removeMessage')}
          confirmLabel={t('opticalOrders.pairs.removeConfirm')}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            act({ type: 'removePair', pairId: removing });
            setRemoving(null);
          }}
        />
      ) : null}
    </div>
  );
}
